import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const HeroSection: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const slides = [
    {
      title: "Creativity Meets Innovation",
      subtitle: "Transforming Ideas into Immersive Realities",
      description: "We are a multidisciplinary organization driven by passion for conceptualizing, ideating, and crafting unique, impactful experiences that redefine event management, advertising, and audio-visual production.",
      background: "/slider-1.jpg",
      cta: "Explore Our Work"
    },
    {
      title: "From Concept to Execution",
      subtitle: "Storytelling that Captivates",
      description: "From the spark of an idea to flawless execution, we deliver storytelling that captivates, engages, and inspires across events, brand narratives, and visual content.",
      background: "/slider-2.jpg",
      cta: "Get Started"
    },
    {
      title: "Setting New Industry Benchmarks",
      subtitle: "Precision Meets Creativity",
      description: "Mirchi Republic blends creativity with precision to bring your vision to life in ways that resonate and endure, setting new industry standards.",
      background: "/slider-3.jpg",
      cta: "View Projects"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    
    return () => clearInterval(timer);
  }, [slides.length]);

  const scrollToNext = () => {
    const nextSection = document.getElementById('events-glimpses');
    nextSection?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative h-screen overflow-hidden">
      {/* Background Slider */}
      <div className="absolute inset-0">
        {slides.map((slide, index) => (
          <motion.div
            key={index}
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${slide.background})` }}
            initial={{ opacity: 0 }}
            animate={{ opacity: index === currentSlide ? 1 : 0 }}
            transition={{ duration: 1 }}
          />
        ))}
        <div className="absolute inset-0 bg-black/40" />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="container-grid">
          <div className="col-span-12 lg:col-span-8 text-center lg:text-left">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <motion.h1
                className="text-4xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight"
                initial={{ clipPath: 'inset(0 100% 0 0)' }}
                animate={{ clipPath: 'inset(0 0 0 0)' }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                {slides[currentSlide].title}
              </motion.h1>
              
              <motion.h2
                className="text-2xl lg:text-4xl font-semibold text-accent-1"
                initial={{ clipPath: 'inset(0 100% 0 0)' }}
                animate={{ clipPath: 'inset(0 0 0 0)' }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                {slides[currentSlide].subtitle}
              </motion.h2>
              
              <motion.p
                className="text-lg lg:text-xl text-neutral-200 max-w-2xl mx-auto lg:mx-0"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                {slides[currentSlide].description}
              </motion.p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-200 ${
              index === currentSlide ? 'bg-brand-1' : 'bg-white/50'
            }`}
          />
        ))}
      </div>

      {/* Scroll Indicator */}
      <motion.button
        onClick={scrollToNext}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white z-20"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <ChevronDown className="w-8 h-8" />
      </motion.button>
    </section>
  );
};

export default HeroSection;